import dynamic from 'next/dynamic';
import { Fragment } from 'react';

// All components as dynamic imports for optimal code splitting
// Only React Fragment remains static since it's built-in
const ButtonLink = dynamic(
  () => import('@uikit/components/ButtonLink/ButtonLink')
);
const Container = dynamic(
  () => import('@uikit/components/Container/Container')
);
const GuideBox = dynamic(() => import('@uikit/components/GuideBox/GuideBox'));
const Headline = dynamic(() => import('@uikit/components/Headline/Headline'));
const HintBox = dynamic(() => import('@uikit/components/HintBox/HintBox'));
const Icon = dynamic(() => import('@uikit/components/Icon/Icon'));
const Image = dynamic(() => import('@uikit/components/Image/Image'));
const NavItem = dynamic(() => import('@uikit/components/NavItem/NavItem'));
const Quote = dynamic(() => import('@uikit/components/Quote/Quote'));
const RichText = dynamic(() => import('@uikit/components/RichText/RichText'));
const Section = dynamic(() => import('@uikit/components/Section/Section'));
const Teaser = dynamic(() => import('@uikit/components/Teaser/Teaser'));
const FlipCardWrapper = dynamic(
  () => import('@/components/FlipCardWrapper/FlipCardWrapper')
);
const ImageMapWrapper = dynamic(
  () => import('@/components/ImageMapWrapper/ImageMapWrapper')
);
const KununuScoreMagnoliaWrapper = dynamic(
  () =>
    import('@/components/KununuScoreMagnoliaWrapper/KununuScoreMagnoliaWrapper')
);
const ProductComparisonWrapper = dynamic(
  () => import('@/components/ProductComparisonWrapper/ProductComparisonWrapper')
);
const SolarVideoChatTeaserSection = dynamic(
  () =>
    import('@/components/SolarVideoChatTeaserSection/SolarVideoChatTeaserSection')
);
const TableWrapper = dynamic(
  () => import('@/components/TableWrapper/TableWrapper')
);
const FiberOpticMap = dynamic(
  () => import('@/uikit/components/FiberOpticMap/FiberOpticMap')
);
const Accordion = dynamic(
  () => import('@uikit/components/Accordion/Accordion')
);
const ACWindPowerInvestCalc = dynamic(
  () => import('@uikit/components/ACWindPowerInvestCalc/ACWindPowerInvestCalc')
);
const AdditionalContents = dynamic(
  () => import('@uikit/components/AdditionalContents/AdditionalContents')
);
const BoxTeaserWrapper = dynamic(
  () => import('@uikit/components/BoxTeaser/BoxTeaserWrapper')
);
const CMPCookieInfo = dynamic(
  () => import('@uikit/components/CMPCookieInfo/CMPCookieInfo')
);
const CMPVendorList = dynamic(
  () => import('@uikit/components/CMPVendorList/CMPVendorList')
);
const CommunityHeatPlannerMap = dynamic(
  () =>
    import('@uikit/components/CommunityHeatPlannerMap/CommunityHeatPlannerMap')
);
const ComponentList = dynamic(
  () => import('@uikit/components/ComponentList/ComponentList')
);
const ContactBlock = dynamic(
  () => import('@uikit/components/ContactBlock/ContactBlock')
);
const ContactTelephone = dynamic(
  () => import('@uikit/components/ContactTelephone/ContactTelephone')
);
const DistrictHeatMap = dynamic(
  () => import('@uikit/components/DistrictHeatMap/DistrictHeatMap')
);
const DisturbanceMapIframe = dynamic(
  () => import('@uikit/components/DisturbanceMapIframe/DisturbanceMapIframe')
);
const DisturbanceMapIframeEng = dynamic(
  () =>
    import('@uikit/components/DisturbanceMapIframeEng/DisturbanceMapIframeEng')
);
const Download = dynamic(() => import('@uikit/components/Download/Download'));
const EegJobSearch = dynamic(
  () => import('@uikit/components/EegJobSearch/EegJobSearch')
);
const EnercityFooterWrapper = dynamic(
  () => import('@uikit/components/EnercityFooter/EnercityFooter.wrapper')
);
const FullScreenStage = dynamic(
  () => import('@uikit/components/FullScreenStage/FullScreenStage')
);
const Group = dynamic(() => import('@uikit/components/Group/Group'));
const GuideBoxList = dynamic(
  () => import('@uikit/components/GuideBoxList/GuideBoxList')
);
const HeatProductTeasers = dynamic(
  () => import('@uikit/components/HeatProductTeasers/HeatProductTeasers')
);
const HeatPumpSavingsCalculator = dynamic(
  () =>
    import('@uikit/components/HeatPumpSavingsCalculator/HeatPumpSavingsCalculator')
);
const Iframe = dynamic(() => import('@uikit/components/Iframe/Iframe'));
const IframeSection = dynamic(
  () => import('@uikit/components/IframeSection/IframeSection')
);
const ImageCompositionMagnoliaWrapper = dynamic(
  () =>
    import('@uikit/components/ImageCompositionGridMagnoliaWrapper/ImageCompositionMagnoliaWrapper')
);
const ImageGallery = dynamic(
  () => import('@uikit/components/ImageGallery/ImageGallery')
);
const ImageGrid = dynamic(
  () => import('@uikit/components/ImageGrid/ImageGrid')
);
const InfoBox = dynamic(() => import('@uikit/components/InfoBox/InfoBox'));
const JobApplySteps = dynamic(
  () => import('@uikit/components/JobApplySteps/JobApplySteps')
);
const JobBenefitTeaser = dynamic(
  () => import('@uikit/components/JobBenefitTeaser/JobBenefitTeaser')
);
const JobCallout = dynamic(
  () => import('@uikit/components/JobCallout/JobCallout')
);
const JobListWrapper = dynamic(
  () => import('@uikit/components/JobListWrapper/JobListWrapper')
);
const JobShiftModel = dynamic(
  () => import('@uikit/components/JobShiftModel/JobShiftModel')
);
const LegacyHeader = dynamic(
  () => import('@uikit/components/LegacyHeader/LegacyHeader.wrapper')
);
const LegacyJobHeader = dynamic(
  () => import('@uikit/components/LegacyJobHeader/LegacyJobHeader.wrapper')
);
const LottieWrapper = dynamic(
  () => import('@uikit/components/LottieWrapper/LottieWrapper')
);
const NoticeBar = dynamic(
  () => import('@uikit/components/NoticeBar/NoticeBar')
);
const ProductTeaser = dynamic(
  () => import('@uikit/components/ProductTeaser/ProductTeaser')
);
const QuoteCarouselWrapper = dynamic(
  () => import('@uikit/components/QuoteCarousel/QuoteCarouselWrapper')
);
const RiveWrapper = dynamic(
  () => import('@uikit/components/RiveWrapper/RiveWrapper')
);
const SectionVisual = dynamic(
  () => import('@uikit/components/SectionVisual/SectionVisual')
);
const ShowcaseElement = dynamic(
  () => import('@uikit/components/ShowcaseElement/ShowcaseElement')
);
const ShowcaseList = dynamic(
  () => import('@uikit/components/ShowcaseList/ShowcaseList')
);
const Signet = dynamic(() => import('@uikit/components/Signets/Signet/Signet'));
const SignetCardDetailGrid = dynamic(
  () =>
    import('@uikit/components/Signets/SignetCardDetailGrid/SignetCardDetailGrid')
);
const SignetCardList = dynamic(
  () => import('@uikit/components/Signets/SignetCardList/SignetCardList')
);
const SignetCarousel = dynamic(
  () => import('@uikit/components/Signets/SignetCarousel/SignetCarousel')
);
const SignetGallery = dynamic(
  () => import('@uikit/components/Signets/SignetGallery/SignetGallery')
);
const SignetGrid = dynamic(
  () => import('@uikit/components/Signets/SignetGrid/SignetGrid')
);
const SignetList = dynamic(
  () => import('@uikit/components/Signets/SignetList/SignetList')
);
const SmartChargingAppSelector = dynamic(
  () =>
    import('@uikit/components/SmartChargingAppSelector/SmartChargingAppSelector')
);
const StageBoxCalculatorNew = dynamic(
  () => import('@uikit/components/StageBoxCalculatorNew/StageBoxCalculatorNew')
);
const StageBoxText = dynamic(
  () => import('@uikit/components/StageBoxText/StageBoxText')
);
const StageBoxWhite = dynamic(
  () => import('@uikit/components/StageBoxWhite/StageBoxWhite')
);
const TariffCard = dynamic(
  () => import('@uikit/components/TariffCard/TariffCard')
);
const TeaserImageCardBigWrapper = dynamic(
  () => import('@uikit/components/TeaserImageCard/TeaserImageCardBig.wrapper')
);
const TeaserImageCardSmallWrapper = dynamic(
  () => import('@uikit/components/TeaserImageCard/TeaserImageCardSmall.wrapper')
);
const TeaserTile = dynamic(
  () => import('@uikit/components/TeaserTile/TeaserTile')
);
const TeaserTileGroup = dynamic(
  () => import('@uikit/components/TeaserTileGroup/TeaserTileGroup')
);
const TeaserTileLarge = dynamic(
  () => import('@uikit/components/TeaserTileLarge/TeaserTileLarge')
);
const TeaserWithImage = dynamic(
  () => import('@uikit/components/TeaserWithImage/TeaserWithImage')
);
const TrustedShopsWidget = dynamic(
  () => import('@uikit/components/TrustedShops/TrustedShopsWidget/TrustedShopsWidget'),
);
const TwoColumnContainer = dynamic(
  () => import('@uikit/components/TwoColumnContainer/TwoColumnContainer')
);
const UmbrellaCampaignStoriesSectionWrapper = dynamic(
  () =>
    import('@uikit/components/UmbrellaCampaignStoriesSectionWrapper/UmbrellaCampaignStoriesSectionWrapper')
);
const UmbrellaCampaignTeaserTileSectionWrapper = dynamic(
  () =>
    import('@uikit/components/UmbrellaCampaignTeaserTileSectionWrapper/UmbrellaCampaignTeaserTileSectionWrapper')
);
const VideoWrapper = dynamic(
  () => import('@uikit/components/Video/Video.wrapper')
);
const VideoTeaser = dynamic(
  () => import('@uikit/components/VideoTeaser/VideoTeaser')
);
const WaermewendeMapIframe = dynamic(
  () => import('@uikit/components/WaermewendeMapIframe/WaermewendeMapIframe')
);
const WedemarkMap = dynamic(
  () => import('@uikit/components/WedemarkMap/WedemarkMap')
);
const GasConsumptionTrafficLight = dynamic(
  () =>
    import('@uikit/composites/GasConsumptionTrafficLight/GasConsumptionTrafficLight')
);
const HeroArea = dynamic(() => import('@uikit/composites/HeroArea/HeroArea'));
const HeroAreaContent = dynamic(
  () => import('@uikit/composites/HeroArea/HeroAreaContent')
);
const SmallHeroArea = dynamic(
  () => import('@uikit/composites/SmallHeroArea/SmallHeroArea')
);
const SplitContainer = dynamic(
  () => import('@uikit/composites/SplitContainer/SplitContainer')
);
const TabsCardsItem = dynamic(
  () => import('@uikit/composites/TabsCardsItem/TabsCardsItem')
);
const TabsContainer = dynamic(
  () => import('@uikit/composites/TabsContainer/TabsContainer')
);

// Form components - disable SSR for these
const Address = dynamic(
  () => import('../components/Forms/FormBuilder/parts/Address/Address'),
  { ssr: false }
);
const Contact = dynamic(
  () => import('../components/Forms/FormBuilder/parts/Contact/Contact'),
  { ssr: false }
);
const CustomCheckBox = dynamic(
  () =>
    import('../components/Forms/FormBuilder/parts/CustomCheckBox/CustomCheckBox'),
  { ssr: false }
);
const CustomRadio = dynamic(
  () => import('../components/Forms/FormBuilder/parts/CustomRadio/CustomRadio'),
  { ssr: false }
);
const CustomSelect = dynamic(
  () =>
    import('../components/Forms/FormBuilder/parts/CustomSelect/CustomSelect'),
  { ssr: false }
);
const CustomText = dynamic(
  () => import('../components/Forms/FormBuilder/parts/CustomText/CustomText'),
  { ssr: false }
);
const Designation = dynamic(
  () => import('../components/Forms/FormBuilder/parts/Designation/Designation'),
  { ssr: false }
);
const FullName = dynamic(
  () => import('../components/Forms/FormBuilder/parts/FullName/FullName'),
  { ssr: false }
);
const InfoText = dynamic(
  () => import('../components/Forms/FormBuilder/parts/InfoText/InfoText'),
  { ssr: false }
);
const InfoTitle = dynamic(
  () => import('../components/Forms/FormBuilder/parts/InfoTitle/InfoTitle'),
  { ssr: false }
);
const Title = dynamic(
  () => import('../components/Forms/FormBuilder/parts/Title/Title'),
  { ssr: false }
);
const Forms = dynamic(() => import('../components/Forms/Forms'), {
  ssr: false,
});
const ProductModalWrapper = dynamic(
  () => import('../components/ProductModalWrapper/ProductModalWrapper')
);
const StageBoxCalculator = dynamic(
  () => import('../components/StageBoxCalculator/StageBoxCalculator')
);
const LogoScrollList = dynamic(
  () => import('@uikit/components/LogoScrollList/LogoScrollList')
);
const LogoList = dynamic(() => import('@uikit/components/LogoList/LogoList'));
const LogoMarquee = dynamic(
  () => import('@uikit/components/LogoMarquee/LogoMarquee')
);

export const CmsComponentsBase = {
  'contact-block': ContactBlock,
  'contact-telephone': ContactTelephone,
  'download-bar': Download,
  'rich-text': RichText,
  'section-visual': SectionVisual,
  'iframe-section': IframeSection,
  'showcase-list': ShowcaseList,
  'showcase-element': ShowcaseElement,
  'stage-box-text': StageBoxText,
  'stage-box-white': StageBoxWhite,
  'stage-box-calculator': StageBoxCalculator,
  'stage-box-calculator-new': StageBoxCalculatorNew,
  'teaser-with-image': TeaserWithImage,
  'teaser-tile': TeaserTile,
  'teaser-tile-group': TeaserTileGroup,
  'teaser-tile-large': TeaserTileLarge,
  button: ButtonLink,
  'product-overview': ProductModalWrapper,
  container: SplitContainer,
  'product-teaser': ProductTeaser,
  'tabs-container': Container,
  headline: Headline,
  icon: Icon,
  image: Image,
  quote: Quote,
  section: Section,
  teaser: Teaser,
  'teaser-image-card-small': TeaserImageCardSmallWrapper,
  'teaser-image-card-big': TeaserImageCardBigWrapper,
  video: VideoWrapper,
  header: LegacyHeader,
  'hint-box': HintBox,
  'nav-item': NavItem,
  'guide-box': GuideBox,
  'two-sided-box-alternative': TwoColumnContainer,
  'component-list': ComponentList,
  'landing-page-header': LegacyHeader,
  table: TableWrapper,
  forms: Forms,
  'eng-forms': Forms,
  'stage-area': Fragment,
  stage: HeroArea,
  'stage-small': SmallHeroArea,
  'guide-box-list': GuideBoxList,
  tabs: TabsContainer,
  'tabs-item': TabsCardsItem,
  'lottie-magnolia': LottieWrapper,
  footer: EnercityFooterWrapper,
  group: Group,
  'hero-area-content': HeroAreaContent,
  'info-box': InfoBox,
  'image-gallery': ImageGallery,
  'image-map-custom': ImageMapWrapper,
  designation: Designation,
  title: Title,
  'full-name': FullName,
  address: Address,
  contact: Contact,
  'custom-text': CustomText,
  'custom-checkbox': CustomCheckBox,
  'custom-select': CustomSelect,
  'custom-radio': CustomRadio,
  'info-text': InfoText,
  'info-title': InfoTitle,
  'notice-bar-area': Fragment,
  'notice-bar': NoticeBar,
  'additional-contents': AdditionalContents,
  'image-grid': ImageGrid,
  'video-teaser': VideoTeaser,
  'job-additional-content-container': Container,
  'image-composition': ImageCompositionMagnoliaWrapper,
  'job-image-grid': ImageCompositionMagnoliaWrapper,
  'job-header': LegacyJobHeader,
  'job-callout': JobCallout,
  'job-apply-steps': JobApplySteps,
  'job-shift-model': JobShiftModel,
  'kununu-score': KununuScoreMagnoliaWrapper,
  'faq-accordion': Accordion,
  'button-link': ButtonLink,
  'job-list-wrapper': JobListWrapper,
  'quote-carousel': QuoteCarouselWrapper,
  iframe: Iframe,
  'fiber-optic-map': FiberOpticMap,
  'district-heat-map': DistrictHeatMap,
  'cmp-cookie-info': CMPCookieInfo,
  'cmp-vendor-list': CMPVendorList,
  'tariff-card': TariffCard,
  'job-benefit-teaser': JobBenefitTeaser,
  'disturbance-map': DisturbanceMapIframe,
  'disturbance-map-eng': DisturbanceMapIframeEng,
  'gas-consumption-traffic-light': GasConsumptionTrafficLight,
  'umbrella-campaign-teaser-tile-section':
    UmbrellaCampaignTeaserTileSectionWrapper,
  'umbrella-campaign-stories-section': UmbrellaCampaignStoriesSectionWrapper,
  'heat-pump-savings-calculator': HeatPumpSavingsCalculator,
  'wedemark-map': WedemarkMap,
  'product-comparison': ProductComparisonWrapper,
  'solar-video-chat-teaser-section': SolarVideoChatTeaserSection,
  'eeg-job-search': EegJobSearch,
  'rive-wrapper': RiveWrapper,
  'flip-card': FlipCardWrapper,
  'community-heat-planner-map': CommunityHeatPlannerMap,
  'ac-wind-power-invest-calc': ACWindPowerInvestCalc,
  'box-teaser': BoxTeaserWrapper,
  'fullscreen-stage': FullScreenStage,
  'heat-product-teasers': HeatProductTeasers,
  'waermewende-map-iframe': WaermewendeMapIframe,
  'smart-charging-app-selector': SmartChargingAppSelector,
  'trusted-shop-widget': TrustedShopsWidget,
  signet: Signet,
  SignetCardList,
  SignetCarousel,
  SignetGallery,
  SignetGrid,
  SignetCardDetailGrid,
  SignetList,
  LogoList,
  LogoMarquee,
  LogoScrollList,
};
